import { Button, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { errorMessage, type FlowResponse, getFlowQueryKey, removeFlowPreapprovalMutation } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { describeAction } from "@/features/chat/actions/actionModel";
import { formatDate } from "@/i18n/format";

/**
 * Write actions this workflow's runs take without pausing for approval (MHS-972): each one tool to one destination,
 * added from Approvals with "Always allow when I run this workflow". Editors can remove one; its runs then ask again.
 * Shown only when there's something to show.
 */
export function FlowPreapprovedActions({ flow }: Readonly<{ flow: FlowResponse }>) {
  const { t } = useTranslation("workflows", { keyPrefix: "preapprovals" });
  const { t: tc } = useTranslation("chat", { keyPrefix: "action" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const remove = useMutation({
    ...removeFlowPreapprovalMutation(),
    onSuccess: () => {
      toast.success(t("removed"));
      return queryClient.invalidateQueries({ queryKey: getFlowQueryKey({ path: { flow_uuid: flow.uuid } }) });
    },
    onError: (error) => toast.error(errorMessage(error, t("removeFailed"))),
  });
  const actions = flow.preapprovedActions ?? [];
  if (actions.length === 0) return null;
  return (
    <PageSection title={t("title")} description={t("description")}>
      <ul aria-label={t("title")} className="flex flex-col gap-2">
        {actions.map((action) => {
          const described = describeAction(action.tool, {});
          const title = described.named
            ? tc(`title.${described.named}`)
            : tc("title.generic", { tool: described.displayName });
          return (
            <li
              key={action.id}
              className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle p-3"
            >
              <ShieldCheck size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />
              <div className="flex min-w-0 flex-1 flex-col">
                <Text as="span" variant="body-sm" className="break-words">
                  {t("entry", { action: title, label: action.label })}
                </Text>
                {action.addedAt && (
                  <Text as="span" variant="caption" tone="tertiary">
                    {t("addedOn", { date: formatDate(action.addedAt) })}
                  </Text>
                )}
              </div>
              {flow.canEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label={t("removeLabel", { action: title, label: action.label })}
                  loading={remove.isPending && remove.variables?.path.preapproval_id === action.id}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate({ path: { flow_uuid: flow.uuid, preapproval_id: action.id } })}
                >
                  {t("remove")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </PageSection>
  );
}
